/**
 * The CloudSign permission catalogue (spec section 6.3).
 *
 * - Keys are stable: never rename a key once released; add a new one and migrate roles instead.
 * - Labels are i18n keys: `perm.<key>` and `permGroup.<group>` (see i18n/en-GB.ts).
 * - `scope` says who can hold it:
 *     tenant   - granted through a tenant role (role_permissions)
 *     platform - held by platform admins only (Oakford); never stored in role_permissions
 *     partner  - held by partner admins (designed in, not built in Phase 0)
 * - `module` is the module key that owns the permission. A tenant permission only takes effect
 *   when that module is enabled for the tenant ('core' is always enabled).
 * - The server is the real check (every route declares one of these keys). The UI only hides things.
 */
const t = (group, module, keys, opts = {}) => keys.map((k) => ({
    key: `${group}.${k}`,
    group,
    module,
    scope: opts.scope ?? 'tenant',
    ...(opts.sensitive?.includes(k) ? { sensitive: true } : {}),
}));
export const PERMISSIONS = [
    // --- Matches Sign In App (spec 6.3) ---
    ...t('audit', 'core', ['view']),
    ...t('today', 'visits', ['view', 'view_locations', 'sign_in_out', 'approve_preregistrations', 'sign_out_all'], {
        sensitive: ['view_locations'],
    }),
    ...t('evacuation', 'evacuation', ['view', 'reports_view', 'reports_manage', 'reports_delete']),
    ...t('history', 'visits', ['view', 'export', 'delete', 'protect']),
    // Timesheets are the reports module's (Phase 2, docs/modules/reports.md); Phase 5 time and attendance builds on them.
    ...t('timesheets', 'reports', ['view', 'export', 'edit']),
    ...t('spaces', 'spaces', ['view', 'manage', 'delete', 'book', 'override_zones', 'bookings_report']),
    ...t('sites', 'sites', ['view', 'manage_devices', 'manage', 'delete_remote']),
    ...t('events', 'events-mgmt', ['view', 'manage', 'delete']),
    ...t('groups', 'groups', ['view', 'manage', 'delete', 'export_members', 'view_members', 'manage_members', 'delete_members', 'manage_sensitive'], { sensitive: ['manage_sensitive'] }),
    ...t('visitors', 'visits', ['edit_notes', 'edit_visits']),
    ...t('api_clients', 'api-clients', ['view', 'manage', 'delete']),
    ...t('forms', 'pre-registration', ['view', 'manage', 'delete']),
    ...t('notifications', 'notifications', ['view', 'manage', 'delete']),
    ...t('users', 'core', ['view', 'manage', 'delete', 'remove_2fa']),
    ...t('account', 'core', ['manage']),
    ...t('safety_check', 'safety-check', ['view', 'manage', 'delete']),
    // --- New for CloudSign ---
    ...t('roles', 'core', ['manage']),
    ...t('deliveries', 'deliveries', ['view', 'manage']),
    ...t('attendance', 'time-attendance', ['view', 'manage']),
    ...t('scheduling', 'scheduling', ['view', 'manage']),
    ...t('webhooks', 'webhooks', ['view', 'manage', 'delete']),
    ...t('integrations', 'integrations', ['view', 'manage']),
    ...t('data_protection', 'data-protection', ['export', 'erase', 'manage_retention'], { sensitive: ['export', 'erase'] }),
    ...t('compliance', 'compliance', ['view', 'manage']),
    ...t('inductions', 'compliance', ['view', 'manage']),
    ...t('permits', 'permits', ['view', 'raise', 'issue', 'close']),
    ...t('lone_working', 'lone-working', ['view', 'respond']),
    ...t('id_checks', 'id-checks', ['view_result', 'view_image'], { sensitive: ['view_result', 'view_image'] }),
    ...t('displays', 'reception-display', ['view', 'manage']),
    ...t('feedback', 'feedback', ['view', 'manage']),
    ...t('insights', 'insights', ['view']),
    ...t('ask_reports', 'ask-reports', ['use']),
    ...t('devices', 'kiosk-devices', ['view', 'remote_actions']),
    // Phase 1 additions (docs/PHASE-1-PLAN.md)
    ...t('notices', 'notices', ['view', 'manage', 'delete']),
    ...t('badges', 'badges', ['manage']),
    ...t('agreements', 'agreements', ['view', 'manage'], { sensitive: ['view'] }),
    // Phase 2 additions (docs/PHASE-2-PLAN.md)
    ...t('preregistrations', 'pre-registration', ['view', 'manage']),
    ...t('omp_link', 'omp-link', ['view', 'manage']),
    // Phase 3 additions (docs/PHASE-3-PLAN.md)
    ...t('evacuation', 'evacuation', ['start', 'mark', 'manage']),
    ...t('contactless', 'contactless', ['manage']),
    ...t('sign_in_points', 'sign-in-points', ['manage']),
    ...t('lone_working', 'lone-working', ['manage']),
    ...t('permits', 'permits', ['manage_types']),
    ...t('occupancy', 'occupancy', ['view', 'manage']),
    ...t('nfc_cards', 'nfc-cards', ['view', 'manage']),
    // Phase 4 additions (docs/PHASE-4-PLAN.md)
    ...t('data_protection', 'data-protection', ['view', 'requests', 'legal_hold'], { sensitive: ['requests'] }),
    ...t('billing', 'billing', ['view', 'manage']),
    ...t('wallet', 'wallet', ['manage']),
    ...t('id_checks', 'id-checks', ['manage']),
    // Shared reception (ADR 0034, docs/SHARED-RECEPTION-PLAN.md). Building roles are not permissions: they live in sr_staff.
    ...t('shared_reception', 'shared-reception', ['view', 'manage']),
    // --- Platform admin (Oakford). Not grantable through tenant roles. ---
    ...t('platform', 'core', [
        'tenants_view',
        'tenants_manage',
        'modules_manage',
        'settings_manage',
        'admins_manage',
        'audit_view',
        'jobs_view',
        'jobs_manage',
        'support_access',
    ], { scope: 'platform' }),
    // Phase 4 (ADR 0031): the platform billing console. Platform-only, owned by the billing module.
    ...t('platform', 'billing', ['billing_view', 'billing_manage'], { scope: 'platform' }),
    // Shared reception (ADR 0034): buildings, invitations, building staff and building devices.
    ...t('platform', 'shared-reception', ['shared_reception_view', 'shared_reception_manage'], { scope: 'platform' }),
    // --- Partner admin (resellers). Designed in, not built in Phase 0. ---
    ...t('partner', 'core', ['tenants_view', 'tenants_manage', 'modules_manage', 'support_access'], { scope: 'partner' }),
];
export const PERMISSION_KEYS = PERMISSIONS.map((p) => p.key);
const byKey = new Map(PERMISSIONS.map((p) => [p.key, p]));
export function getPermission(key) {
    return byKey.get(key);
}
export function isPermissionKey(key) {
    return byKey.has(key);
}
export const TENANT_PERMISSION_KEYS = PERMISSIONS.filter((p) => p.scope === 'tenant').map((p) => p.key);
export const PLATFORM_PERMISSION_KEYS = PERMISSIONS.filter((p) => p.scope === 'platform').map((p) => p.key);
export const PARTNER_PERMISSION_KEYS = PERMISSIONS.filter((p) => p.scope === 'partner').map((p) => p.key);
/** Permission groups in catalogue order, for the role editor. */
export const PERMISSION_GROUPS = [...new Set(PERMISSIONS.map((p) => p.group))];
/** Special route permission markers used in route config (not stored in roles). */
export const ROUTE_ACCESS = {
    /** No session needed (rate-limited). */
    public: 'public',
    /** Any session at any login stage. Only the auth step endpoints use this; they check the stage themselves. */
    session: 'session',
    /** Any signed-in user with a completed login (tenant or platform area), no specific permission. */
    authenticated: 'authenticated',
    /**
     * A paired device (kiosk) with `Authorization: Device <token>` (ADR 0008). Runs in tenant scope limited to the
     * device's site, with no user permissions; the route must also set `config.module`. Enforced from Phase 1 task C1b.
     */
    device: 'device',
    /**
     * Phase 2 (ADR 0015): a Companion app token (`Authorization: Companion <token>`), acting only on its own member.
     * The route must also set `config.module`. Refused with 401 until task C2 implements the hook.
     */
    companion: 'companion',
    /**
     * Phase 2 (ADR 0019): an API key (`Authorization: Bearer csk_...`); the route must set `config.module` and
     * `config.apiScope`. Refused with 401 until task C2 implements the hook.
     */
    api: 'api',
    /**
     * Phase 3 (ADR 0022): a contactless session token (`Authorization: Contactless <token>`) from the public page, acting
     * only within its own site and visit. The route must also set `config.module`. Refused with 401 until task C4.
     */
    contactless: 'contactless',
    /**
     * Shared reception (ADR 0034): a signed-in session in the `building` area (2FA always). The route also sets
     * `config.buildingRole`; the server checks the role from sr_staff on every request. Runs in `building` scope.
     * Refused with 401 until task C-SR1 implements the hook.
     */
    building: 'building',
    /**
     * Shared reception (ADR 0034): the shared tablet's building token (`Authorization: SharedKiosk <token>`), `building` scope.
     * Refused with 401 until task C-SR1 implements the hook.
     */
    building_device: 'building_device',
    /**
     * Shared reception (ADR 0034): a building marshal's phone (`Authorization: SrPhone <token>`, Companion app), `building`
     * scope, acting as that staff account. Refused with 401 until task C-SR1 implements the hook.
     */
    building_phone: 'building_phone',
};
/** Built-in roles created for every new tenant. */
export const BUILTIN_ROLES = {
    administrator: {
        key: 'administrator',
        nameKey: 'role.administrator',
        siteScope: 'all',
        /** Administrator always has every tenant permission; stored rows are ignored. */
        permissions: 'all',
        editable: false,
    },
    standard: {
        key: 'standard',
        nameKey: 'role.standard',
        siteScope: 'all',
        permissions: [
            'today.view',
            'today.sign_in_out',
            'history.view',
            'evacuation.view',
            'sites.view',
            'groups.view',
            'groups.view_members',
            'events.view',
            'deliveries.view',
        ],
        editable: true,
    },
};
//# sourceMappingURL=permissions.js.map